import React, { useState, useEffect, useCallback } from "react";
import { fileUrl } from "@/lib/api";
import { ZoomIn, ZoomOut, RotateCw, X, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";

// Fullscreen zoom/rotate viewer for photos. `photos` = array of storage paths.
export default function PhotoLightbox({ photos = [], index = 0, open, onClose }) {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);

  const reset = () => { setZoom(1); setRot(0); };

  useEffect(() => { if (open) { setI(index); reset(); } }, [open, index]);

  const go = useCallback((d) => {
    setI((prev) => (prev + d + photos.length) % photos.length);
    reset();
  }, [photos.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && photos.length > 1) go(1);
      else if (e.key === "ArrowLeft" && photos.length > 1) go(-1);
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(z + 0.25, 4));
      else if (e.key === "-") setZoom((z) => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go, onClose, photos.length]);

  if (!open || photos.length === 0) return null;

  const btn = "w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors backdrop-blur";

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col" data-testid="photo-lightbox" onClick={onClose}>
      {/* top bar */}
      <div className="flex items-center justify-between p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm font-medium" data-testid="lightbox-counter">
          {photos.length > 1 ? `Foto ${i + 1} / ${photos.length}` : "Foto Soal"}
        </span>
        <button className={btn} onClick={onClose} data-testid="lightbox-close" aria-label="Tutup"><X className="w-5 h-5" /></button>
      </div>

      {/* image area */}
      <div className="flex-1 flex items-center justify-center overflow-auto px-2" onClick={(e) => e.stopPropagation()}>
        {photos.length > 1 && (
          <button className={`${btn} mr-2 shrink-0`} onClick={() => go(-1)} data-testid="lightbox-prev" aria-label="Sebelumnya"><ChevronLeft className="w-6 h-6" /></button>
        )}
        <img
          src={fileUrl(photos[i])}
          alt={`Foto soal ${i + 1}`}
          style={{ transform: `scale(${zoom}) rotate(${rot}deg)` }}
          className="max-h-[70vh] max-w-full object-contain transition-transform origin-center select-none"
          data-testid="lightbox-image"
          draggable={false}
        />
        {photos.length > 1 && (
          <button className={`${btn} ml-2 shrink-0`} onClick={() => go(1)} data-testid="lightbox-next" aria-label="Berikutnya"><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>

      {/* controls */}
      <div className="flex items-center justify-center gap-3 p-5" onClick={(e) => e.stopPropagation()}>
        <button className={btn} onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))} data-testid="lightbox-zoom-out" aria-label="Perkecil"><ZoomOut className="w-5 h-5" /></button>
        <span className="text-white text-sm w-14 text-center" data-testid="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
        <button className={btn} onClick={() => setZoom((z) => Math.min(z + 0.25, 4))} data-testid="lightbox-zoom-in" aria-label="Perbesar"><ZoomIn className="w-5 h-5" /></button>
        <button className={btn} onClick={() => setRot((r) => (r + 90) % 360)} data-testid="lightbox-rotate" aria-label="Putar"><RotateCw className="w-5 h-5" /></button>
        <button className={btn} onClick={reset} data-testid="lightbox-reset" aria-label="Atur ulang"><RefreshCw className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
